/**
 * Contrato e consultas da esteira.
 *
 * Duas regras de exibição moram aqui, e não nas telas, para que as duas telas não possam
 * discordar:
 *
 * · `entrega_prevista` só existe depois da liberação e da geração da tarefa de kickoff. Nulo é **"o relógio não
 *   começou"** — nunca "hoje", nunca uma estimativa. `textoDoPrazo` é a única função autorizada
 *   a transformar esse estado em frase, e ela não inventa data.
 * · a etapa e de quem é a vez vêm derivadas do servidor, da linha do tempo. A tela não recalcula
 *   nada: se recalculasse, o painel do cliente e o console interno poderiam contar histórias
 *   diferentes sobre a mesma implantação.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ErroDaApi, editarJson, enviarFormulario, enviarJson, pedirJson } from "./api";
/* O caminho das telas do motor de formulário mora em `kickoff.ts`, que é quem as conhece. A
   dependência é de mão única — `kickoff.ts` não importa nada daqui — para que não exista ciclo
   entre os dois módulos do núcleo. */
import {
  CAMINHO_DOS_FORMULARIOS,
  TRILHA_DE_INFRAESTRUTURA,
  caminhoDaRevisao,
  caminhoDaTrilha,
  useTrilhas,
} from "./kickoff";
import { podeNaSessao, useSessao } from "./sessao";

/* ------------------------------------------------------------------ vocabulário */

/* A reunião voltou como etapa por decisão de 09/10/2026 (SUS-14400). O cliente consulta a
   agenda e a ata, mas só a equipe interna agenda. O servidor deriva o estado. */
export const ETAPAS = ["contrato", "infraestrutura", "kickoff", "reuniao", "execucao"] as const;

const esquemaEtapa = z.enum(ETAPAS);
const esquemaEstadoDaEtapa = z.enum([
  "concluida",
  "em_andamento",
  "nao_iniciada",
  "bloqueada",
]);
const esquemaVez = z.enum(["cliente", "geovendas", "ninguem"]);

export type Etapa = z.infer<typeof esquemaEtapa>;
export type EstadoDaEtapa = z.infer<typeof esquemaEstadoDaEtapa>;
export type Vez = z.infer<typeof esquemaVez>;

/* ------------------------------------------------------------------- catálogo */

const esquemaModuloDoCatalogo = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  descricao: z.string().nullable(),
  ordem: z.number(),
});

const esquemaRecursoDoCatalogo = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  /* `null` = recurso transversal, que não pende de um módulo único: "Estoque por Data" serve ao
     Força de Vendas e ao B2B, e o GuiaRep vale para a operação inteira. Exigir string aqui faria
     o catálogo inteiro falhar na validação por causa de dois itens — e a tela de cadastro é a
     primeira que abre. */
  modulo_chave: z.string().min(1).nullable(),
  /* Só registros legados anteriores ao campo podem chegar sem classificação. A tela exige o
     valor em toda criação ou edição e torna a lacuna visível para saneamento. */
  jira_project_id: z.string().min(1).nullable(),
  ordem: z.number(),
  descricao: z.string().nullable().default(null),
  ativo: z.boolean().default(true),
});

const esquemaOpcao = z.object({ valor: z.string().min(1), rotulo: z.string().min(1) });

const esquemaPessoa = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  email: z.string().min(1),
  telefone: z.string().nullable().optional(),
});

export const esquemaCatalogo = z.object({
  modulos: z.array(esquemaModuloDoCatalogo),
  recursos_plus: z.array(esquemaRecursoDoCatalogo),
  projetos_jira_dos_recursos_plus: z.array(esquemaOpcao),
  ambientes: z.array(esquemaOpcao),
  situacoes: z.array(esquemaOpcao),
  /* O "consultor da conta": quem fechou a venda e quem o cliente procura. Uma lista só desde a
     0105 (requisito 3 da Amanda) — consultor e responsável pela venda viraram o mesmo campo. */
  consultores: z.array(esquemaPessoa),
});

export type Catalogo = z.infer<typeof esquemaCatalogo>;
export type ModuloDoCatalogo = z.infer<typeof esquemaModuloDoCatalogo>;
export type RecursoDoCatalogo = z.infer<typeof esquemaRecursoDoCatalogo>;

/* ------------------------------------------------------------------- respostas */

const esquemaOrganizacao = z.object({
  id: z.string().min(1),
  razao_social: z.string().min(1),
  nome_fantasia: z.string().nullable(),
  cnpj: z.string().min(1),
  cidade: z.string().nullable(),
  uf: z.string().nullable(),
});

const esquemaModuloContratado = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  jira_task_key: z.string().nullable().optional(),
  jira_project_id: z.string().nullable().optional(),
  jira_status_id: z.string().nullable().optional(),
  jira_status_nome: z.string().nullable().optional(),
  jira_responsavel_nome: z.string().nullable().optional(),
  jira_responsavel_email: z.string().nullable().optional(),
  jira_observado_em: z.string().nullable().optional(),
  /* Quando o módulo foi concluído e repassado ao atendimento (item 6 da Amanda). */
  repassado_em: z.string().nullable().optional(),
});

const esquemaRecursoContratado = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  /* `null` pelo mesmo motivo de `esquemaRecursoDoCatalogo`: recurso transversal não tem módulo. */
  modulo_chave: z.string().min(1).nullable(),
  jira_project_id: z.string().nullable().optional(),
  jira_issue_key: z.string().nullable().optional(),
  jira_status_id: z.string().nullable().optional(),
  jira_status_nome: z.string().nullable().optional(),
  jira_observado_em: z.string().nullable().optional(),
});

/* Metadados do PDF do contrato assinado. `null` é implantação anterior à regra — não é erro. */
export const esquemaContrato = z.object({
  nome: z.string().min(1),
  tamanho: z.number(),
  anexado_em: z.string(),
  substituido_em: z.string().nullable().default(null),
});

export type ContratoAnexado = z.infer<typeof esquemaContrato>;

export const LIMITE_DO_CONTRATO = 10 * 1024 * 1024;

/** A mesma conferência do servidor, adiantada: o servidor confere os bytes (`%PDF-`) de novo. */
export function erroDoContrato(arquivo: File | null): string | undefined {
  if (!arquivo) return "Anexe o contrato assinado em PDF.";
  const pareceGrande = arquivo.size > LIMITE_DO_CONTRATO;
  const pareceVazio = arquivo.size === 0;
  const naoPdf = !/\.pdf$/i.test(arquivo.name) && arquivo.type !== "application/pdf";
  if (naoPdf || pareceVazio || pareceGrande) {
    return "O contrato deve ser um PDF, com até 10 MB.";
  }
  return undefined;
}

export const esquemaImplantacao = z.object({
  id: z.string().min(1),
  organizacao: esquemaOrganizacao,
  jira_epic_key: z.string().nullable(),
  contrato_assinado_em: z.string(),
  prazo_dias: z.number(),
  ambiente_tipo: z.string(),
  erp: z.string().nullable(),
  situacao_cliente: z.string(),
  ambiente_liberado_em: z.string().nullable(),
  entrega_prevista: z.string().nullable(),
  estado: z.string(),
  modulos: z.array(esquemaModuloContratado),
  recursos_plus: z.array(esquemaRecursoContratado),
  etapa_atual: esquemaEtapa,
  vez: esquemaVez,
  contrato: esquemaContrato.nullable().default(null),
});

export const esquemaImplantacaoCriada = z.object({
  implantacao: esquemaImplantacao,
  organizacao_criada: z.boolean(),
  convite: z.object({
    enviado: z.boolean(),
    email: z.string().min(1),
    papel_chave: z.string().nullable(),
    motivo: z.string().nullable(),
    modo_teste: z.boolean(),
  }),
});

export type ImplantacaoCriada = z.infer<typeof esquemaImplantacaoCriada>;

export const esquemaItemDaListagem = z.object({
  id: z.string().min(1),
  organizacao_id: z.string().min(1),
  organizacao: z.string().min(1),
  cnpj: z.string().min(1),
  modulos: z.array(z.string()),
  etapa_atual: esquemaEtapa,
  etapa_nome: z.string().min(1),
  vez: esquemaVez,
  estado: z.string(),
  jira_epic_key: z.string().nullable(),
  consultor: z.string().nullable(),
  entrega_prevista: z.string().nullable(),
  dias_restantes: z.number().nullable(),
  relogio_iniciado: z.boolean(),
  criada_em: z.string(),
});

export type ItemDaListagem = z.infer<typeof esquemaItemDaListagem>;

export const esquemaEsteira = z.object({
  acompanhamento_liberado: z.boolean().default(false),
  implantacao_id: z.string().min(1),
  organizacao: esquemaOrganizacao,
  etapas: z.array(
    z.object({
      etapa: esquemaEtapa,
      nome: z.string().min(1),
      ordem: z.number(),
      estado: esquemaEstadoDaEtapa,
      vez: esquemaVez,
      resumo: z.string().min(1),
      ocorrido_em: z.string().nullable(),
    }),
  ),
  etapa_atual: esquemaEtapa,
  vez: esquemaVez,
  proximo_passo: z.string().min(1),
  concluidas: z.number(),
  total: z.number(),
  prazo: z.object({
    contrato_assinado_em: z.string(),
    prazo_dias: z.number(),
    ambiente_liberado_em: z.string().nullable(),
    kickoff_tarefa_gerada_em: z.string().nullable().optional(),
    entrega_prevista: z.string().nullable(),
    inicio_do_prazo_em: z.string().nullable().optional(),
    relogio_iniciado: z.boolean(),
    dias_restantes: z.number().nullable(),
  }),
  pendencias: z.array(
    z.object({
      referencia: z.string().min(1),
      titulo: z.string().min(1),
      descricao: z.string().nullable(),
      de_quem: esquemaVez,
      aberta_em: z.string(),
    }),
  ),
  modulos: z.array(esquemaModuloContratado),
  recursos_plus: z.array(esquemaRecursoContratado),
  consultor: esquemaPessoa.nullable(),
  /* Quem o cliente procura, já resolvido pelo servidor: o consultor da conta (o responsável
     pela venda). É este o campo que a tela usa; a partir de Treinamento a tela do módulo troca
     pelo responsável da tarefa (`andamentoDoModulo.ts`). */
  contato: esquemaPessoa.nullable(),
  contrato: esquemaContrato.nullable().default(null),
});

export type Esteira = z.infer<typeof esquemaEsteira>;
export type EtapaDaEsteira = Esteira["etapas"][number];
export type PrazoDaEsteira = Esteira["prazo"];

/* --------------------------------------------------------------------- chamadas */

export interface NovaImplantacao {
  empresa: {
    cnpj: string;
    razao_social: string;
    nome_fantasia: string | null;
    cidade: string | null;
    uf: string | null;
    segmento: string | null;
  };
  /* Opcional: sem nenhum campo preenchido a tela manda `null`. Vira a "Matriz" da organização
     nova; cliente que já tem endereço não ganha um segundo. */
  endereco: {
    cep: string | null;
    logradouro: string | null;
    numero: string | null;
    complemento: string | null;
    bairro: string | null;
    cidade: string | null;
    uf: string | null;
  } | null;
  contrato_assinado_em: string;
  prazo_dias: number;
  modulos: string[];
  recursos_plus: string[];
  ambiente_tipo: string;
  erp: string | null;
  /* Vai para a descrição do épico no Jira, sob "OBSERVAÇÕES:". */
  observacoes: string | null;
  situacao_cliente: string;
  contato: {
    nome: string;
    cargo: string | null;
    email: string;
    telefone: string | null;
    pode_convidar_colegas: boolean;
    vincular_existente?: boolean;
  };
  /* O consultor da conta, obrigatório: quem FECHOU o negócio. Não é quem está operando a tela —
     a carteira do console recorta por ele. */
  consultor_id: string;
}

export const CHAVE_CATALOGO = ["implantacoes", "catalogo"] as const;
export const CHAVE_LISTAGEM = ["implantacoes", "listagem"] as const;
export const CHAVE_MINHA_ESTEIRA = ["implantacoes", "minha"] as const;

/** `habilitado` falso não pergunta: a rota exige `implantacao:criar`, e quem não a tem só colheria um 403. */
export function useCatalogo(habilitado = true) {
  return useQuery<Catalogo>({
    queryKey: CHAVE_CATALOGO,
    queryFn: ({ signal }) => pedirJson("/implantacoes/catalogo", esquemaCatalogo, { signal }),
    // O catálogo muda quando alguém publica um módulo novo, não a cada navegação.
    staleTime: 5 * 60_000,
    enabled: habilitado,
  });
}

export const CHAVE_RECURSOS_PLUS = ["catalogo", "recursos-plus"] as const;

const RAIZ_DOS_RECURSOS = "/implantacoes/catalogo/recursos-plus";

/**
 * O catálogo COMPLETO, com os inativos — a lista de quem administra.
 *
 * `useCatalogo` continua devolvendo só os ativos: ela alimenta o cadastro de implantação, onde
 * recurso desativado não pode ser vendido. São duas listas de propósito, e a diferença entre elas
 * é o que faz "desativar" ser o caminho seguro de remover.
 */
export function useRecursosPlus(habilitado = true) {
  return useQuery<RecursoDoCatalogo[]>({
    queryKey: CHAVE_RECURSOS_PLUS,
    queryFn: ({ signal }) =>
      pedirJson(RAIZ_DOS_RECURSOS, z.array(esquemaRecursoDoCatalogo), { signal }),
    enabled: habilitado,
  });
}

export interface RecursoParaGravar {
  nome: string;
  modulo_chave: string | null;
  jira_project_id: string;
  ordem: number;
  descricao: string | null;
  ativo: boolean;
}

/* Gravar invalida as DUAS listas: a de quem administra e a que o cadastro de implantação usa
   para vender. Invalidar só uma deixaria a tela de cadastro oferecendo o que acabou de ser
   desativado — a segunda lista que envelhece em silêncio. */
function invalidarCatalogo(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: CHAVE_RECURSOS_PLUS });
  void cliente.invalidateQueries({ queryKey: CHAVE_CATALOGO });
}

export function useCriarRecursoPlus() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: RecursoParaGravar & { chave: string }) =>
      enviarJson(RAIZ_DOS_RECURSOS, esquemaRecursoDoCatalogo, corpo),
    onSuccess: () => invalidarCatalogo(cliente),
  });
}

export function useAlterarRecursoPlus() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ chave, ...corpo }: RecursoParaGravar & { chave: string }) =>
      editarJson(`${RAIZ_DOS_RECURSOS}/${encodeURIComponent(chave)}`, esquemaRecursoDoCatalogo, corpo),
    onSuccess: () => invalidarCatalogo(cliente),
  });
}

/**
 * Quantas linhas a carteira pede — o máximo que o servidor aceita (`LIMITE_MAXIMO`).
 *
 * O padrão do servidor é 50, e a carteira passou a ser ordenada NA TELA por data, consultor e
 * etapa (spec de 28/09, 6.6). Ordenar só as 50 mais recentes daria uma ordem que parece certa e
 * não é: o cliente mais atrasado podia estar na 51ª linha. Com a carteira inteira na mão, a
 * ordenação é da lista toda.
 */
export const LIMITE_DA_CARTEIRA = 200;

/* ------------------------------------------------------------- indicadores da carteira */

/** Os quatro números do topo da carteira; as definições estão em `api/.../indicadores.py`. */
export const esquemaIndicadoresDaCarteira = z.object({
  ativas: z.number(),
  aguardando_infra: z.number(),
  infra_com_cliente: z.number(),
  infra_com_geovendas: z.number(),
  em_risco_de_prazo: z.number(),
  sem_relogio: z.number(),
  dias_de_alerta: z.number(),
  /* `null` = sem dado suficiente para uma média honesta; `motivo_sem_tempo_medio` diz por quê. */
  tempo_medio_de_resposta_dias: z.number().nullable(),
  respostas_medidas: z.number(),
  esperas_em_aberto: z.number(),
  motivo_sem_tempo_medio: z.string().nullable(),
});

export type IndicadoresDaCarteira = z.infer<typeof esquemaIndicadoresDaCarteira>;

/* Prefixada por `CHAVE_LISTAGEM` (com um objeto, que nenhuma busca em texto pode igualar): quem
   invalida a listagem — criar, liberar, aceitar — invalida os números do topo junto. */
const UM_DECIMAL = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** "4,2 dias"; sem média é "—", nunca zero. */
export function textoDoTempoMedio(dias: number | null): string {
  if (dias === null) return "—";
  return `${UM_DECIMAL.format(dias)} ${dias === 1 ? "dia" : "dias"}`;
}

export const CHAVE_INDICADORES = [...CHAVE_LISTAGEM, { indicadores: true }] as const;

/** Mesmo recorte da tabela (o padrão do papel no servidor): número e linhas contam a mesma carteira. */
export function useIndicadoresDaCarteira() {
  return useQuery<IndicadoresDaCarteira>({
    queryKey: CHAVE_INDICADORES,
    queryFn: ({ signal }) =>
      pedirJson("/implantacoes/indicadores", esquemaIndicadoresDaCarteira, { signal }),
  });
}

export function useImplantacoes(opcoes: { busca?: string } = {}) {
  const busca = opcoes.busca?.trim() ?? "";
  return useQuery<ItemDaListagem[]>({
    queryKey: [...CHAVE_LISTAGEM, busca],
    queryFn: ({ signal }) => {
      const parametros = new URLSearchParams({ limite: String(LIMITE_DA_CARTEIRA) });
      if (busca) parametros.set("busca", busca);
      return pedirJson(`/implantacoes?${parametros.toString()}`, z.array(esquemaItemDaListagem), {
        signal,
      });
    },
  });
}

/**
 * A esteira da própria organização. **Não recebe id** — e é isso que a torna segura:
 * não existe parâmetro para trocar, e o servidor resolve a implantação pela sessão.
 *
 * `404` não é falha: é a organização que ainda não tem esteira aberta. A tela mostra a
 * explicação em vez de um erro.
 */
export function useMinhaEsteira(habilitada = true) {
  return useQuery<Esteira | null>({
    queryKey: CHAVE_MINHA_ESTEIRA,
    queryFn: async ({ signal }) => {
      try {
        return await pedirJson("/implantacoes/minha", esquemaEsteira, { signal });
      } catch (causa) {
        if (causa instanceof ErroDaApi && causa.status === 404) return null;
        throw causa;
      }
    },
    retry: false,
    enabled: habilitada,
  });
}

/**
 * A implantação de quem está logado na área do cliente, sem pedir o que o papel não pode ver.
 *
 * A consulta tem `ata:ver` e `kickoff:ver`, mas não `esteira:ver` (matriz da spec §4): pedir a
 * esteira a ela só colhia um 403 mostrado como erro. A implantação dela vem das trilhas do
 * kickoff, que a resposta já identifica. Só com a sessão resolvida a falta de permissão é uma
 * afirmação sobre esta pessoa; antes disso ninguém é "consulta".
 *
 * · `pendente` — a sessão ou a origem da implantação ainda não responderam. Com `semAcesso` não
 *   há origem nenhuma para esperar, então não fica pendente.
 * · `erro` — a falha da origem, para a tela mostrar a frase do servidor.
 * · `implantacaoId` — `""` enquanto não há implantação (ou não se sabe qual).
 * · `acessoDeConsulta` — falta `esteira:ver`; a tela deve avisar e não mostrar o andamento.
 * · `semAcesso` — consulta que também não tem `kickoff:ver`: não há de onde tirar a implantação.
 */
export function useImplantacaoDoCliente() {
  const sessao = useSessao();
  const acessoDeConsulta = !!sessao.data && !podeNaSessao(sessao.data, "esteira:ver");
  const semAcesso = acessoDeConsulta && !podeNaSessao(sessao.data, "kickoff:ver");
  const esteira = useMinhaEsteira(!sessao.isPending && !acessoDeConsulta);
  const trilhas = useTrilhas(acessoDeConsulta && !semAcesso);
  const origem = acessoDeConsulta ? trilhas : esteira;
  return {
    pendente: sessao.isPending || (!semAcesso && origem.isPending),
    erro: origem.isError ? origem.error : null,
    implantacaoId: (esteira.data?.implantacao_id ?? trilhas.data?.implantacao_id) ?? "",
    acessoDeConsulta,
    semAcesso,
  };
}

/**
 * O cliente de uma implantação, pela carteira que a tela já carregou.
 *
 * Existe para as telas internas por id dizerem DE QUEM elas estão falando sem uma consulta
 * própria. A alternativa era pedir `GET /implantacoes/{id}` — que exige `contrato:ver`, permissão
 * que o papel de infraestrutura não tem — ou a esteira, que seria uma requisição a mais e um
 * modo de falha a mais para escrever um nome num cabeçalho.
 *
 * A carteira é a mesma consulta que trouxe a pessoa até aqui, então o react-query a serve do
 * cache; quem abre a URL direto paga uma listagem que ele já tem permissão de ver. `null` quando
 * a implantação não está na carteira de quem abriu — a tela cai no título genérico em vez de
 * afirmar um nome que não conferiu.
 */
export function useClienteDaCarteira(implantacaoId: string): ItemDaListagem | null {
  const carteira = useImplantacoes();
  return carteira.data?.find((item) => item.id === implantacaoId) ?? null;
}

/** Rota do download: o navegador baixa direto (`Content-Disposition: attachment`). */
export function caminhoDoContrato(implantacaoId: string): string {
  return `/api/implantacoes/${encodeURIComponent(implantacaoId)}/contrato`;
}

/** O corpo JSON e o PDF viajam num pedido só (multipart): sem PDF, o servidor não grava nada. */
export function formularioDaImplantacao(corpo: NovaImplantacao, contrato: File): FormData {
  const formulario = new FormData();
  formulario.append("dados", JSON.stringify(corpo));
  formulario.append("contrato", contrato);
  return formulario;
}

export function useCriarImplantacao() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ corpo, contrato }: { corpo: NovaImplantacao; contrato: File }) =>
      enviarFormulario(
        "/implantacoes",
        esquemaImplantacaoCriada,
        formularioDaImplantacao(corpo, contrato),
      ),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE_LISTAGEM });
    },
  });
}

/* -------------------------------------------------------------- formato e frases */

const FORMATO_DE_DATA = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "America/Sao_Paulo",
});

/** `2026-08-04` → `04/08/2026`. Data ISO sem hora é lida como dia civil, sem virar fuso. */
export function formatarData(iso: string | null): string | null {
  if (!iso) return null;
  const partes = iso.slice(0, 10).split("-");
  if (partes.length !== 3) return null;
  const [ano, mes, dia] = partes.map(Number);
  if (!ano || !mes || !dia) return null;
  return FORMATO_DE_DATA.format(new Date(Date.UTC(ano, mes - 1, dia, 12)));
}

export function formatarCnpj(cnpj: string): string {
  const digitos = cnpj.replace(/\D/g, "");
  if (digitos.length !== 14) return cnpj;
  return `${digitos.slice(0, 2)}.${digitos.slice(2, 5)}.${digitos.slice(5, 8)}/${digitos.slice(
    8,
    12,
  )}-${digitos.slice(12)}`;
}

export interface TextoDoPrazo {
  /** A data, ou `null` quando o relógio ainda não começou. Nunca uma estimativa. */
  data: string | null;
  /** Título do bloco. */
  titulo: string;
  /** A frase que explica o número — ou a ausência dele. */
  explicacao: string;
}

/**
 * O bloco de prazo em palavras.
 *
 * Esta função **não produz data nenhuma** antes da liberação e da geração da tarefa de kickoff.
 * Ela mostra quantos dias foram contratados e quais marcos faltam para começarem.
 */
export function textoDoPrazo(prazo: PrazoDaEsteira, voz: VozDaEsteira = "cliente"): TextoDoPrazo {
  if (!prazo.relogio_iniciado || !prazo.entrega_prevista) {
    return {
      data: null,
      titulo: "Entrega prevista",
      explicacao:
        voz === "interna"
          ? `O prazo de ${prazo.prazo_dias} dias começa após a liberação do ambiente e a geração da tarefa de kickoff.`
          : `Os ${prazo.prazo_dias} dias do contrato começam após a liberação do ambiente e a geração da tarefa de kickoff.`,
    };
  }
  const inicio = formatarData(prazo.inicio_do_prazo_em ?? null);
  return {
    data: formatarData(prazo.entrega_prevista),
    titulo: "Entrega prevista",
    explicacao: `${textoDosDiasRestantes(prazo.dias_restantes)}${inicio ? ` · contando desde ${inicio}` : ""}.`,
  };
}

function textoDosDiasRestantes(restante: number | null): string {
  if (restante === null) return "Prazo em andamento";
  if (restante === 0) return "A entrega prevista é hoje";
  if (restante > 0) return `Faltam ${restante} ${restante === 1 ? "dia" : "dias"}`;
  return `${Math.abs(restante)} ${restante === -1 ? "dia de atraso" : "dias de atraso"}`;
}

/**
 * Para onde o card de cada etapa leva — e, por consequência, quais etapas já se resolvem por uma
 * tela do portal.
 *
 * O destino e o conjunto são a MESMA declaração: uma etapa só "tem tela" se existir um caminho
 * para onde mandar quem clicar. Enquanto as duas coisas eram listas separadas, dava para marcar
 * a etapa como resolvida e não oferecer caminho nenhum — que é exatamente o beco em que a Amanda
 * tropeçou no teste de 28/08/2026: o card anunciava "sua vez" e clicar não fazia nada.
 *
 * A etapa de kickoff leva ao índice porque são várias trilhas, uma por módulo contratado; a de
 * infraestrutura leva direto à trilha dela, que é uma só.
 */
export const DESTINO_DA_ETAPA = {
  infraestrutura: caminhoDaTrilha(TRILHA_DE_INFRAESTRUTURA),
  kickoff: CAMINHO_DOS_FORMULARIOS,
  reuniao: "/app/reunioes",
} as const satisfies Partial<Record<Etapa, string>>;

/** As etapas que já se resolvem por uma tela do portal. */
export const ETAPAS_COM_TELA: ReadonlySet<Etapa> = new Set(
  Object.keys(DESTINO_DA_ETAPA) as Etapa[],
);

/**
 * A permissão de LEITURA que a tela de cada etapa exige do servidor.
 *
 * Espelha `questionario/servico.py::permissao_de_leitura`, e existe pelo mesmo motivo do
 * `podeNaSessao`: a tela não pode **oferecer** o que o servidor vai negar. A etapa 2 tornou isso
 * visível — ela é governada por `infra:*` (spec §4), e o `key_user` tem `kickoff:ver` mas não tem
 * `infra:ver`. Enquanto o card não olhava permissão, ele mostrava "Responder agora" para o key
 * user, que clicava e caía num aviso de erro. O beco continuava lá; só tinha ficado mais bonito.
 *
 * Isto **não protege nada** — quem nega é o servidor, com a checagem por trilha. É só a diferença
 * entre a tela dizer "não é com você" e a tela mentir e depois se desculpar.
 */
export const PERMISSAO_DA_ETAPA = {
  infraestrutura: "infra:ver",
  kickoff: "kickoff:ver",
  reuniao: "ata:ver",
} as const satisfies Record<keyof typeof DESTINO_DA_ETAPA, string>;

/** A permissão exigida pela tela da etapa, ou `null` quando a etapa não tem tela. */
export function permissaoDaEtapa(etapa: Etapa): string | null {
  return etapa in PERMISSAO_DA_ETAPA
    ? PERMISSAO_DA_ETAPA[etapa as keyof typeof PERMISSAO_DA_ETAPA]
    : null;
}

/** O que o card diz a quem não tem a permissão da etapa — em vez de um botão que dá 403. */
export const AVISO_SEM_PERMISSAO_NA_ETAPA =
  "Esta etapa é preenchida por quem responde pela TI da sua empresa. Peça a quem administra " +
  "o acesso, se for para ser com você.";

/** O mesmo aviso no console: lá quem lê não preenche, lê — e quem lê as respostas é a consultoria. */
export const AVISO_SEM_PERMISSAO_NA_ETAPA_INTERNO =
  "Ler as respostas desta etapa é da consultoria. Se você precisa delas, peça a quem conduz " +
  "a implantação.";

/** O caminho da etapa, ou `null` quando o portal ainda não tem tela para ela. */
export function destinoDaEtapa(etapa: Etapa): string | null {
  return etapa in DESTINO_DA_ETAPA
    ? DESTINO_DA_ETAPA[etapa as keyof typeof DESTINO_DA_ETAPA]
    : null;
}

/**
 * O destino da etapa para quem lê a esteira pelo console interno.
 *
 * Defeito 3.2 da especificação de 28/09: o console usa o mesmo componente da esteira, e o
 * destino era a rota do CLIENTE — "Abrir formulário" na infraestrutura tirava a consultora da
 * visão interna e carregava o menu do cliente. Dentro do console, a leitura das respostas é a
 * revisão interna, que já traz todas as trilhas que o perfil pode ler (inclusive a de
 * infraestrutura, para quem tem `infra:ver`).
 */
export function destinoInternoDaEtapa(etapa: Etapa, implantacaoId: string): string | null {
  if (etapa === "reuniao") return `/admin/implantacoes/${encodeURIComponent(implantacaoId)}/reunioes`;
  return ETAPAS_COM_TELA.has(etapa) ? caminhoDaRevisao(implantacaoId) : null;
}

/** A permissão que a revisão interna cobra no servidor — a tela não oferece o que ele nega. */
export const PERMISSAO_DA_REVISAO_INTERNA = "kickoff:revisar";

/** O aviso não é sobre a etapa: é sobre o portal ainda não ter a tela dela. */
export const AVISO_SEM_TELA =
  "Esta etapa ainda não se preenche por aqui. Quem conduz é o time da GEOvendas, " +
  "por e-mail ou na reunião — o portal mostra o andamento e vai abrir o preenchimento " +
  "em uma próxima entrega.";

/* A esteira é a mesma; quem lê, não. O console interno mostrava a esteira DO CLIENTE para a
   consultora — "Sua vez" numa etapa que é do cliente, "nossa infraestrutura" e "seu ambiente"
   invertendo os papéis. Numa carteira com catorze clientes isso vira erro de leitura. Os estados
   não mudaram: mudou a pessoa a quem a frase se dirige. Ver o 4.2 dos ajustes da fase 1. */
export type VozDaEsteira = "cliente" | "interna";

export const ROTULO_DA_VEZ: Record<Vez, string> = {
  cliente: "Sua vez",
  geovendas: "Com a GEOvendas",
  ninguem: "Nada pendente",
};

const ROTULO_DA_VEZ_INTERNO: Record<Vez, string> = {
  cliente: "Com o cliente",
  geovendas: "Com a GEOvendas",
  ninguem: "Nada pendente",
};

export function rotuloDaVez(vez: Vez, voz: VozDaEsteira = "cliente"): string {
  return voz === "interna" ? ROTULO_DA_VEZ_INTERNO[vez] : ROTULO_DA_VEZ[vez];
}

/* O resumo de cada etapa e o próximo passo chegam do servidor (`implantacoes/esteira.py`) já
   escritos para o CLIENTE — "nossa infraestrutura", "o seu TI", "você acompanha por aqui". No
   console isso inverte os papéis: a consultora lia "Nossa infraestrutura está montando…" como
   se fosse o recado dela ao cliente, e não o que ela tem de fazer. A derivação é a mesma para os
   dois lados (é o que impede o console e o cliente de discordarem), então a troca de voz é só de
   texto e acontece aqui, casada pela frase exata. Frase que o servidor mudar e este mapa não
   conhecer passa como veio — a voz do cliente é o pior caso, não um texto em branco. Spec 4.4. */
const TEXTO_DA_ESTEIRA_NA_VOZ_INTERNA: Readonly<Record<string, string>> = {
  // resumos das etapas
  "Ambiente validado e liberado pela infraestrutura da GEOvendas.":
    "Ambiente validado e liberado pela infraestrutura.",
  "Dados enviados. Nossa infraestrutura está montando e validando o ambiente.":
    "Dados enviados pelo cliente. Falta a infraestrutura montar e validar o ambiente.",
  "Nossa infraestrutura devolveu com pendência. Confira o que falta e reenvie.":
    "A infraestrutura devolveu com pendência. Falta o cliente corrigir e reenviar.",
  "Preenchimento começado. Você pode parar e voltar quando quiser.":
    "O cliente começou a preencher.",
  "Precisamos dos acessos ao seu servidor e à base do ERP para montar o ambiente. É a etapa que destrava o resto.":
    "Falta o cliente enviar os acessos ao servidor e à base do ERP. É a etapa que destrava o resto.",
  "Respostas enviadas. Precisou mudar algo? Fale com o consultor responsável.":
    "Respostas enviadas pelo cliente.",
  "Em preenchimento. Tudo fica salvo — dá para parar e voltar.": "O cliente está preenchendo.",
  "Já liberado — não precisa esperar a reunião. Só os módulos que você contratou.":
    "Liberado para o cliente, com os módulos contratados. Não depende da reunião.",
  "Configuração em execução pela nossa equipe. Você acompanha por aqui.":
    "Configuração em execução pela equipe.",
  "Com as respostas fechadas, a configuração entra na fila da nossa equipe.":
    "Com as respostas fechadas, a configuração entra na fila da equipe.",
  // próximos passos
  "Preencher os dados de infraestrutura com o seu TI. O prazo depende da liberação e da geração da tarefa de kickoff.":
    "O cliente preenche os dados de infraestrutura com o TI dele.",
  "Nossa infraestrutura está montando e validando o ambiente. Avisamos assim que liberar.":
    "A infraestrutura monta e valida o ambiente. Quando estiver pronto, libere o ambiente nesta " +
    "tela para registrar o primeiro marco do prazo.",
  "Responder o kickoff dos módulos contratados. Já está liberado.":
    "O cliente responde o kickoff dos módulos contratados.",
  "Nossa equipe executa a configuração a partir das suas respostas.":
    "A equipe executa a configuração a partir das respostas do cliente.",
  "Nada pendente com você agora.": "Nada pendente com o cliente agora.",
};

/** O texto da esteira na voz de quem lê. Na voz do cliente, exatamente o que o servidor mandou. */
export function textoDaEsteira(texto: string, voz: VozDaEsteira = "cliente"): string {
  return voz === "interna" ? (TEXTO_DA_ESTEIRA_NA_VOZ_INTERNA[texto] ?? texto) : texto;
}

export const ROTULO_DO_ESTADO: Record<EstadoDaEtapa, string> = {
  concluida: "Concluído",
  em_andamento: "Em andamento",
  nao_iniciada: "Não iniciado",
  bloqueada: "Bloqueado",
};

/** Cor do chip por estado — a mesma escala do design system, sem inventar tom novo. */
export const CHIP_DO_ESTADO: Record<EstadoDaEtapa, "ok" | "aguardando" | "neutro"> = {
  concluida: "ok",
  em_andamento: "aguardando",
  nao_iniciada: "neutro",
  bloqueada: "neutro",
};
